import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { IcbtTasks } from "api/types";
import { useGetAllProgressForUsers } from "api/useIcbtQueries";
import {
  FeedbackModalStack,
  ModalState,
} from "components/patients/patientCard/icbt/modals/FeedbackModal/FeedbackModalStack";
import { Patient } from "core/types/index";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { unique } from "remeda";
import { styled } from "styled-components";
import {
  doneCommunicationCategories,
  type Categories,
} from "../hooks/useCategorizedDoneCommunication";
import { useIcbtLanguage } from "../hooks/useTreatment";
import {
  TaskItem,
  HandlerProps,
  UserAnswersByRequestIdByUserId,
} from "./TaskItem";
import { useFlag } from "@mindlercare/unleash-client";
import { useGetAllProgramsAndTreatmentsQuery } from "../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { QuestionnaireCounts } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { useSortedIcbtDoneTasks } from "../hooks/useSortedIcbtDoneTasks";
import { TasksTitles } from "./TasksTitles";

const Categories = styled.div`
  display: flex;
  margin-bottom: 20px;
  gap: 8px;
`;

const Category = styled.div<{ $active: boolean }>`
  padding: 10px 20px;
  cursor: pointer;
  color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_WHITE
      : defaultTheme.COLORS.GRAY_BLACK};
  border: ${(props) =>
    props.$active
      ? `2px solid ${defaultTheme.COLORS.GRAY_BLACK}`
      : `1px solid ${defaultTheme.COLORS.GRAY_MEDIUM}`};
  background-color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_BLACK
      : defaultTheme.COLORS.GRAY_WHITE};
  border-radius: 100px;
  &:hover {
    color: ${defaultTheme.COLORS.GRAY_WHITE};
    background-color: ${defaultTheme.COLORS.GRAY_BLACK};
  }
`;

const FeedbackList = styled.ul`
  display: flex;
  padding: 0;
  flex-direction: column;
`;

export const DoneTasksTable = ({
  icbtTasks,
  patients,
  questionnaireCounts,
  userAnswersByRequestIdByUserId,
}: {
  icbtTasks?: IcbtTasks[];
  patients?: { firstName: string | null; lastName: string | null }[];
  questionnaireCounts: QuestionnaireCounts;
  userAnswersByRequestIdByUserId: UserAnswersByRequestIdByUserId;
}) => {
  const { t } = useTranslation();

  const [modalPropsState, setModalPropsState] = useState<ModalState>({
    programId: "",
    treatmentId: "",
    userId: undefined,
    icbtRequestId: "",
    icbtFeedbackDate: "",
    icbtFeedbackDateIgnored: false,
    patientName: "",
    assignIcbtModalOpen: false,
    feedbackModalOpen: false,
  });

  const [tab, setTabState] = useQueryState(
    "state",
    parseAsStringLiteral(doneCommunicationCategories).withDefault("all"),
  );

  const handleTabChange = (newTab: Categories) => setTabState(newTab);

  const { data: progressByUser } = useGetAllProgressForUsers(
    unique(icbtTasks?.map((icbt) => icbt.patientUserId) ?? []),
  );
  const language = useIcbtLanguage();

  // TODO: loading state and error handling plz
  const { data: programsAndTreatments } = useGetAllProgramsAndTreatmentsQuery(
    language.codeISO2,
  );

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  const categories = {
    all: t("ALL"),
    week: t("THIS_WEEK"),
    month: t("THIS_MONTH"),
    ...(ignoreFeedbackDateFeatureEnabled
      ? { noFeedbackNeeded: t("NO_FEEDBACK_NEEDED_TAB") }
      : {}),
  };

  const doneCategorized = useSortedIcbtDoneTasks({
    icbtTasks: icbtTasks,
    patientsToIncludeInCommunication: (patients as Patient[]) ?? [],
  });

  const feedbackData = doneCategorized.get(tab);

  return (
    <>
      <Categories>
        {Object.entries(categories).map(([key, label]) => (
          <Category
            key={key}
            $active={tab === key}
            onClick={() => handleTabChange(key as Categories)}
          >
            {`${label} ${doneCategorized.get(key as Categories)?.length ?? ""}`}
          </Category>
        ))}
      </Categories>
      <FeedbackList>
        <TasksTitles />
        {feedbackData?.map((item, i) => {
          return (
            <TaskItem
              displayDropdownUpwards={
                feedbackData.length > 1 && feedbackData.length === i + 1
              }
              key={item.icbtRequestId}
              done
              item={item}
              progress={progressByUser?.[item.patientUserId]?.[item.programId]}
              programsAndTreatments={programsAndTreatments}
              questionnaireCounts={questionnaireCounts}
              userAnswersByRequestIdByUserId={userAnswersByRequestIdByUserId}
              onClick={({ icbtTasks }: HandlerProps) => {
                setModalPropsState({
                  icbtRequestId: icbtTasks.icbtRequestId,
                  patientName: `${item.patient?.firstName} ${item.patient?.lastName}`,
                  programId: icbtTasks.programId,
                  treatmentId: icbtTasks.treatmentId,
                  userId: Number(icbtTasks.patientUserId),
                  feedbackModalOpen: true,
                  icbtFeedbackDate: icbtTasks.feedbackDate,
                  icbtFeedbackDateIgnored: icbtTasks.feedbackDateIgnored,
                });
              }}
            />
          );
        })}
      </FeedbackList>
      <FeedbackModalStack
        modalState={modalPropsState}
        setModalState={setModalPropsState}
      />
    </>
  );
};
